import React, { useMemo } from 'react';
import { DelinquentReportIcon } from '@common-fe/common-fe';

import { ROUTES } from '@/common';
import { OrganizationTypes } from '@/common/constants';
import { useCurrentOrganization } from '@/modules/core';
import { useNewBadge } from '@/utils';

import { useFavoriteReportHandler } from '../../hooks';
import { ReportTypeKeys } from '../../report.types';
import ReportCardBase, { ReportCardBaseProps } from '../ReportCardBase';

import { useDelinquentClaimsBriefQuery } from './queries';

interface Props extends ReportCardBaseProps {}

const TEST_ID = 'delinquent-claims-report-card';

// Badge start date (UTC) — the NEW badge will display for 60 days from this date
const BADGE_START_DATE = '2026-08-24T00:00:00Z';

const DelinquentClaimsReportCard: React.FC<Props> = ({ isFavoriteMode }) => {
  const {
    observingOrganization: { type },
  } = useCurrentOrganization();
  const isSystem = useMemo(() => type === OrganizationTypes.system, [type]);

  const { data, isLoading, isSuccess } = useDelinquentClaimsBriefQuery();
  const isNew = useNewBadge(BADGE_START_DATE);

  const childrenNode = useMemo(() => {
    if (!isSystem) {
      const items = [
        {
          label: 'Repayments required',
          value: `${data.repaymentRequiredCount}`,
        },
      ];

      if (data.lockedPursesCount != null && isFavoriteMode) {
        items.push({
          label: 'Locked purses',
          value: `${data.lockedPursesCount}`,
        });
      }

      return (
        <ReportCardBase.DescriptionBase
          isLoading={isLoading}
          testId={TEST_ID}
          title="Claims in escalation"
          items={items}
        />
      );
    }
    return null;
  }, [data.lockedPursesCount, data.repaymentRequiredCount, isFavoriteMode, isLoading, isSystem]);

  const { isFavorite, handleToggleReportKey } = useFavoriteReportHandler(ReportTypeKeys.DelinquentClaims);

  if (!data.isDelinquentClaimsShowed || isLoading || !isSuccess) {
    return false;
  }
  return (
    <ReportCardBase
      isNew={isNew}
      testId={TEST_ID}
      isWideMode={isFavoriteMode}
      isFavorite={isFavorite}
      onChangeFavorite={handleToggleReportKey}
      url={ROUTES.DELINQUENT_CLAIMS_REPORT}
      icon={
        <DelinquentReportIcon
          size={isFavoriteMode ? '50px' : '40px'}
          color={isFavoriteMode ? 'iconAccent' : 'iconDisabled'}
        />
      }
      title="Delinquent Claims"
      description="Card swipe claims in escalation"
    >
      {childrenNode}
    </ReportCardBase>
  );
};

export default DelinquentClaimsReportCard;
